import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Award, Download, Save, RefreshCw } from "lucide-react";
import { AdminShell } from "@/components/AdminShell";
import { downloadBlob } from "@/components/SignOnSetup";

const OPS = [["exactly", "exactly"], ["at_least", "at least"], ["more_than", "more than"]];

function AwardCard({ i, a, result, onChange }) {
  const [open, setOpen] = useState(false);
  const elig = result?.eligible || [];
  return (
    <div className="border border-black/10 bg-white" data-testid={`award-card-${i}`}>
      <div className="border-b border-black/10 p-4">
        <div className="text-[10px] font-black uppercase tracking-widest text-[#FF3B30]">Award #{i + 1}</div>
        <label className="mt-2 block text-[10px] font-semibold uppercase tracking-widest text-zinc-500">Nickname of award</label>
        <input value={a.nickname} onChange={(e) => onChange({ nickname: e.target.value })} maxLength={80} placeholder="e.g. Iron Marshal" data-testid={`award-nickname-${i}`} className="mt-1 w-full border border-black/20 px-3 py-2 text-sm font-semibold outline-none focus:border-black" />
        <label className="mt-3 block text-[10px] font-semibold uppercase tracking-widest text-zinc-500">Criteria</label>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-sm">
          <span>All volunteers who signed on for</span>
          <select value={a.op} onChange={(e) => onChange({ op: e.target.value })} data-testid={`award-op-${i}`} className="border border-black/20 bg-white px-2 py-1.5">
            {OPS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <input type="number" min={0} max={500} value={a.value} onChange={(e) => onChange({ value: Math.max(0, Math.min(500, Number(e.target.value) || 0)) })} data-testid={`award-value-${i}`} className="w-20 border border-black/20 px-2 py-1.5 text-center font-mono" />
          <select value={a.metric} onChange={(e) => onChange({ metric: e.target.value })} data-testid={`award-metric-${i}`} className="border border-black/20 bg-white px-2 py-1.5">
            <option value="roles">roles</option>
            <option value="days">days</option>
          </select>
        </div>
        {a.value === 0 ? <p className="mt-1 text-[11px] text-amber-700" data-testid={`award-zero-hint-${i}`}>0 means nobody qualifies — only volunteers who signed on for at least one role can win.</p> : null}
      </div>
      <button onClick={() => setOpen(!open)} className="flex w-full items-center justify-between px-4 py-3 text-left text-sm hover:bg-zinc-50" data-testid={`award-results-toggle-${i}`}>
        <span><b className="font-mono text-lg" data-testid={`award-count-${i}`}>{elig.length}</b> eligible now</span>
        <span className="text-xs text-[#002FA7] underline">{open ? "Hide" : "Show"} names</span>
      </button>
      {open ? (
        <ul className="max-h-72 divide-y divide-black/5 overflow-y-auto border-t border-black/10 text-xs" data-testid={`award-results-${i}`}>
          {elig.map((p, j) => (
            <li key={j} className="flex flex-wrap gap-x-3 px-4 py-2">
              <span className="font-semibold">{p.name}</span>
              <span className="text-zinc-500">{p.role_count} role{p.role_count !== 1 ? "s" : ""} · {p.day_count} day{p.day_count !== 1 ? "s" : ""}</span>
              <span className="ml-auto font-mono text-zinc-500">{p.mobile}</span>
            </li>
          ))}
          {!elig.length ? <li className="px-4 py-3 text-zinc-500">Nobody meets this yet (saved criteria).</li> : null}
        </ul>
      ) : null}
    </div>
  );
}

export default function AdminAwards() {
  const { eventId } = useParams();
  const [ev, setEv] = useState(null);
  const [data, setData] = useState(null);
  const [awards, setAwards] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/admin/events/${eventId}/awards`).then(({ data }) => { setData(data); setAwards(data.awards); setDirty(false); })
      .catch(() => toast.error("Could not load awards"));
  }, [eventId]);
  useEffect(() => {
    load();
    api.get(`/admin/events/${eventId}`).then(({ data }) => setEv(data)).catch(() => {});
  }, [eventId, load]);

  const change = (i, patch) => { setAwards((xs) => xs.map((a, j) => (j === i ? { ...a, ...patch } : a))); setDirty(true); };

  async function save() {
    setBusy(true);
    try { await api.put(`/admin/events/${eventId}/awards`, { awards }); toast.success("Award criteria saved"); load(); }
    catch (e) { toast.error(e.response?.data?.detail?.[0]?.msg || e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  }
  async function runReport() {
    setBusy(true);
    try {
      if (dirty) await api.put(`/admin/events/${eventId}/awards`, { awards });
      await downloadBlob(`/admin/events/${eventId}/awards/export`, `${ev?.slug || "event"}-volunteer-awards.xlsx`);
      toast.success("Awards report downloaded");
      if (dirty) load();
    } catch (e) { toast.error("Could not run the awards report"); }
    finally { setBusy(false); }
  }

  return (
    <AdminShell event={ev ? { id: eventId, name: ev.name, status: ev.status, slug: ev.slug } : { id: eventId }} active="awards" title="Volunteer awards">
      <main className="mx-auto max-w-6xl px-6 py-8" data-testid="awards-page">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-[#002FA7]">Recognition</div>
            <h1 className="mt-1 flex items-center gap-2 font-display text-4xl font-black tracking-tighter"><Award className="h-8 w-8" /> Volunteer awards</h1>
            <p className="mt-2 max-w-2xl text-sm text-zinc-600">
              Set your three awards and run the report at any time, even while the event is running. A role only counts when the volunteer was rostered on it <b>and signed on that day</b>.
              {data ? <> Right now <b data-testid="awards-signed-total">{data.signed_on_volunteers}</b> volunteer{data.signed_on_volunteers !== 1 ? "s have" : " has"} signed on.</> : null}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={load} data-testid="awards-refresh-btn" className="inline-flex items-center gap-1.5 border border-black/20 px-3 py-2 text-xs hover:border-black"><RefreshCw className="h-3.5 w-3.5" /> Refresh</button>
            <button onClick={save} disabled={busy || !dirty} data-testid="awards-save-btn" className="inline-flex items-center gap-1.5 border border-black px-4 py-2 text-sm font-semibold hover:bg-black hover:text-white disabled:opacity-40"><Save className="h-4 w-4" /> Save criteria</button>
            <button onClick={runReport} disabled={busy} data-testid="awards-run-report-btn" className="inline-flex items-center gap-1.5 border border-[#FF3B30] bg-[#FF3B30] px-4 py-2 text-sm font-black uppercase tracking-wider text-white hover:bg-white hover:text-[#FF3B30] disabled:opacity-40"><Download className="h-4 w-4" /> Run awards report</button>
          </div>
        </div>
        {dirty ? <p className="mt-3 text-xs text-amber-700" data-testid="awards-unsaved">Unsaved changes — counts below update after you save (Run awards report saves automatically).</p> : null}
        <div className="mt-6 grid gap-4 lg:grid-cols-3">
          {awards.map((a, i) => <AwardCard key={i} i={i} a={a} result={data?.results?.[i]} onChange={(p) => change(i, p)} />)}
        </div>
        <p className="mt-6 text-xs text-zinc-500">The Excel file has one sheet per award with name, email, mobile, roles and days signed on.</p>
      </main>
    </AdminShell>
  );
}
